/**
 * GLTFMeshActor - actor prefab that loads a GLTFMeshComponent as its root.
 * Wraps GLTFMeshComponent so models can be spawned with minimal boilerplate.
 */

import { SceneComponent } from '../components/SceneComponent.js';
import { ModelMeshComponent } from '../components/visual/ModelMeshComponent.js';
import { ClassRegistry, EngineClass } from '../systems/ClassRegistry.js';

import { Actor } from './Actor.js';

import type { GenericMaterialType } from '../materials.js';
import type { ActorOptions } from './Actor.js';
import type { ComponentPhysicsOptions } from '../physics/PhysicsTypes.js';

export interface ModelMeshActorOptions extends ActorOptions {
  modelUrl?: string;
  material?: GenericMaterialType;
  physicsOptions?: ComponentPhysicsOptions;
}

@EngineClass('ModelMeshActor')
export class ModelMeshActor extends Actor {

  public override initialize(options?: ModelMeshActorOptions): void {
    super.initialize(options);

    const mesh = ModelMeshComponent.create({
      name: 'RootComponent',
    });
    this.setRootComponent(mesh, true);

    if (options?.modelUrl) {
      mesh.modelUrl = options.modelUrl;
    }

    if (options?.material) {
      mesh.setMaterial(options.material);
    }

    if (options?.physicsOptions) {
      mesh.replacePhysicsOptions(options.physicsOptions);
    }
  }

  public override postLoad(): void {
    // Hook for post-load initialization if needed
    super.postLoad();
  }

  public override setRootComponent(component: SceneComponent, copyTransform: boolean): this {
    if (!(component instanceof ModelMeshComponent)) {
      console.error('ModelMeshActor can only have ModelMeshComponent as root');
    }
    super.setRootComponent(component, copyTransform);
    return this;
  }

  public getMeshComponent(): ModelMeshComponent | null {
    return this.rootComponent instanceof ModelMeshComponent ? this.rootComponent : null;
  }

  public async waitForLoad(): Promise<void> {
    const meshComponent = this.getMeshComponent();
    if (meshComponent) {
      await meshComponent.waitForLoad();
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_GLTFMesh';
  }
}

ClassRegistry.addClassesToRedirect({
  'ENGINE.GLTFMeshActor': 'ENGINE.ModelMeshActor',
});

/**
 * @deprecated Use {@link ModelMeshActor} instead.
 */
export const GLTFMeshActor = ModelMeshActor;

/**
 * @deprecated Use {@link ModelMeshActor} instead.
 */
export type GLTFMeshActor = ModelMeshActor;

/**
 * @deprecated Use {@link ModelMeshActorOptions} instead.
 */
export type GLTFMeshActorOptions = ModelMeshActorOptions;
